import React from 'react';
import { useTranslation } from 'react-i18next';

import { Mail } from 'lucide-react';

import { useSupportContactEnabled } from '../../hooks/useIdlc1129Features';
import { green01, greyP4, greyP5 } from '../../theme.ts';
import { SUPPORT_EMAIL, buildSupportMailto } from '../../utils/support.ts';

interface FooterProps {
  projectPage: boolean;
  activeTab?: 'projects' | 'catalogue' | 'exports' | 'analytics';
  /** The 50 px bar above the copyright line (hints on the project pages). */
  statusBar?: boolean;
}

const Footer: React.FC<FooterProps> = ({ projectPage, activeTab = 'projects', statusBar = true }) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const supportContact = useSupportContactEnabled();

  return (
    <footer className="sticky bottom-0 z-30 w-full">
      {statusBar && (
        <div className="bg-black flex border-b border-t" style={{ borderColor: greyP5, height: '50px' }}>
          <div
            className="bg-black hidden md:flex items-center justify-start px-4 border-r"
            style={{ width: '240px', borderColor: greyP5 }}
          >
            {projectPage && activeTab !== 'catalogue' && (
              <div className="flex items-center">
                <div className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">i</span>
                </div>
                <span className="text-white font-medium" style={{ fontSize: '12px' }}>
                  {t('footer.leftClickSelect')}
                </span>
              </div>
            )}
          </div>
          <div className="flex-1 flex items-center justify-start pl-4 md:pl-8 overflow-x-auto">
            <div className="flex items-center space-x-4 md:space-x-16"></div>
          </div>
        </div>
      )}

      <div className="h-12 bg-black flex items-center justify-center border-t" style={{ borderColor: greyP5 }}>
        <div className="w-full flex items-center justify-between px-4">
          <span className="text-sm" style={{ color: greyP4 }}>
            {t('footer.copyright', { year: currentYear })}
          </span>
          {supportContact ? (
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              onClick={(e) => {
                // Fill in subject and diagnostics at click time (current page, language).
                e.currentTarget.href = buildSupportMailto();
              }}
              className="group flex items-center gap-2 text-sm"
            >
              <span className="hidden sm:inline" style={{ color: greyP4 }}>
                {t('footer.needHelp')}
              </span>
              <Mail size={14} style={{ color: green01 }} />
              <span className="text-gray-200 group-hover:underline" style={{ textDecorationColor: green01 }}>
                {SUPPORT_EMAIL}
              </span>
            </a>
          ) : (
            <span className="text-sm" style={{ color: greyP4 }}>
              {SUPPORT_EMAIL}
            </span>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
